import React, { useState } from 'react';
import {
  Button,
  Column,
  Copy,
  Flex,
  Row,
  Stack,
  SvgPencil,
  SvgX,
} from '@grubhubprod/gh-cookbook-react';
import { Square } from '@grubhubprod/gh-cookbook-react/dist';
import { useParams } from 'react-router-dom';
import {
  IDepartment,
  SHORT_SEARCH_DELAY,
  useDepartmentSearch,
} from 'src/utilities/useSearchHooks';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import { sdk } from 'src/utilities/sdk';
import { pushError, pushSuccess } from 'src/utilities/notifications';
import { SimpleDialog } from 'src/components/SimpleDialog/SimpleDialog';
import { useScreenSizes } from 'src/utilities/useScreenSizes';
import EnterpriseDepartmentForm from 'src/components/EnterpriseSignupSteps/EnterpriseCompanyDetailsStep/EnterpriseDepartmentForm';
import {
  EnterpriseSetupTable,
  IEnterpriseSetupTableRowProps,
} from 'src/components/EnterpriseSetupTable/EnterpriseSetupTable';

interface EnterpriseDepartmentTableProps {
  hasItemsCb: (isValid: boolean) => void;
}

export function EnterpriseDepartmentTable({
  hasItemsCb,
}: EnterpriseDepartmentTableProps): JSX.Element {
  const { corpid } = useParams<{ corpid: string }>();

  const { isDesktop } = useScreenSizes();

  return (
    <EnterpriseSetupTable<IDepartment>
      corpId={corpid as string}
      searchPlaceholder="Search by department name"
      noResultsMessage="You haven't added any departments yet"
      addButtonText="Add department"
      searchResource={() => useDepartmentSearch(corpid as string, true)}
      tableMaxHeight={isDesktop ? '205px' : 'initial'}
      hasItemsCb={hasItemsCb}
      headerColumns={[
        {
          columnName: 'Department name',
          columnWidth: 10,
        },
      ]}
      rowElementRender={(
        department,
        isEditing,
        toggleEditState,
        updateSearch
      ) => (
        <DepartmentTableRow
          department={department}
          isEditing={isEditing}
          toggleEditState={toggleEditState}
          updateSearch={updateSearch}
          corpid={corpid as string}
        />
      )}
      footerAddItemRender={(toggleAddState, updateSearch) => (
        <EnterpriseDepartmentForm
          onSuccess={() => {
            updateSearch();
            toggleAddState();
          }}
          onCancel={toggleAddState}
        />
      )}
    />
  );
}

interface IDepartmentTableRow extends IEnterpriseSetupTableRowProps {
  department: IDepartment;
  corpid: string;
}

function DepartmentTableRow({
  department,
  isEditing,
  toggleEditState,
  updateSearch,
  corpid,
}: IDepartmentTableRow): JSX.Element {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const [departmentDeletionInProgress, setDepartmentDeletionInProgress] =
    useState(false);

  function doSearch() {
    updateSearch({ page: 1 });
    toggleEditState();
  }

  async function deleteDepartment(id: string): Promise<void> {
    setDepartmentDeletionInProgress(true);
    try {
      await sdk.http.delete(`/corp-admin/${corpid}/departments/${id}`);
      setTimeout(() => {
        doSearch();
        pushSuccess(`Your department was removed`);
        setShowConfirmationModal(false);
      }, SHORT_SEARCH_DELAY);
    } catch (error) {
      pushError(`Your department was not removed. Try again.`);
      setShowConfirmationModal(false);
      setDepartmentDeletionInProgress(false);
    }
  }

  return (
    <>
      <Square data-testid={`department-row-${department.id}`} inset="sm">
        <Row gutter={8}>
          {isEditing ? (
            <EnterpriseDepartmentForm
              onSuccess={doSearch}
              onCancel={() => toggleEditState()}
              department={department}
            />
          ) : (
            <>
              <Column gutter={8} phone={9} style={{ overflow: 'hidden' }}>
                <span
                  style={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  {department.name}
                </span>
              </Column>
              {/* should be updated and rely on a deletable property once deletable property works correctly */}
              {department.name !== 'Unassigned' && (
                <Column gutter={8} phone={3}>
                  <Flex
                    width="100%"
                    flexDirection="row"
                    justifyContent="flex-end"
                    alignItems="center"
                  >
                    <Stack x="md">
                      <Tooltip
                        roundedContainer
                        content={
                          <Copy variant="body" color="invertedPrimaryText">
                            Edit
                          </Copy>
                        }
                      >
                        <div>
                          <Button
                            variant="tertiary"
                            className="tableActionButtons"
                            onClick={() => toggleEditState(department.id)}
                          >
                            <SvgPencil aria-label="Edit" />
                          </Button>
                        </div>
                      </Tooltip>
                    </Stack>
                    <Tooltip
                      roundedContainer
                      content={
                        <Copy variant="body" color="invertedPrimaryText">
                          Delete
                        </Copy>
                      }
                    >
                      <div>
                        <Button
                          variant="tertiary"
                          className="tableActionButtons"
                          onClick={() => setShowConfirmationModal(true)}
                        >
                          <SvgX aria-label="Remove" />
                        </Button>
                      </div>
                    </Tooltip>
                  </Flex>
                </Column>
              )}
            </>
          )}
        </Row>
      </Square>
      {showConfirmationModal && (
        <SimpleDialog
          id={department.id}
          primaryActionFlavor="danger"
          title={`Remove "${department.name}"`}
          description="You’re about to remove this department."
          primaryActionText="Yes, remove"
          isPrimaryButtonLoading={departmentDeletionInProgress}
          buttonsDirection="row"
          secondaryActionText="Cancel"
          disablePrimaryActionButton={departmentDeletionInProgress}
          disableSecondaryActionButton={departmentDeletionInProgress}
          onSecondaryActionClick={() => {
            setShowConfirmationModal(false);
          }}
          onPrimaryActionClick={(id) => deleteDepartment(id)}
        />
      )}
    </>
  );
}
